import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Building2, CalendarDays, Clock3, MapPin, Plus, RefreshCw, Trash2 } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "./SchoolCalendar.css";

interface SchoolOption {
  id: number;
  name: string;
  subdomain?: string;
  status?: string;
}

interface CalendarEvent {
  id: string;
  name: string;
  date: string;
  endDate?: string | null;
  description?: string;
  location?: string;
  category?: string;
  responsibleTeacher?: string;
}

interface AcademicTerm {
  id: number;
  name?: string | null;
  term?: number | string;
  year?: number | string;
  startDate?: string | null;
  endDate?: string | null;
}

interface CalendarResponse {
  schoolName: string;
  events: CalendarEvent[];
  terms: AcademicTerm[];
}

const formatDate = (value?: string | null) => {
  if (!value) return "Date not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date not set";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const SuperadminCalendar: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>("");
  const [data, setData] = useState<CalendarResponse>({ schoolName: "School", events: [], terms: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    name: "",
    date: "",
    endDate: "",
    location: "",
    category: "general",
    responsibleTeacher: "",
    description: ""
  });

  const fetchSchools = useCallback(async () => {
    const response = await axios.get<SchoolOption[]>(getApiUrl("superadmin/schools"), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const nextSchools = response.data || [];
    setSchools(nextSchools);
    setSelectedSchoolId((current) => current || (nextSchools[0] ? String(nextSchools[0].id) : ""));
  }, [token]);

  const fetchCalendar = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setLoading(false);
      setData({ schoolName: "School", events: [], terms: [] });
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await axios.get<CalendarResponse>(getApiUrl("school/calendar"), {
        headers: { Authorization: `Bearer ${token}` },
        params: { tenantId }
      });
      setData(response.data);
    } catch (err: any) {
      console.error("Error fetching superadmin calendar:", err);
      setError(err.response?.data?.message || "Failed to load school calendar");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSchools().catch((err: any) => {
      console.error("Error fetching schools for calendar:", err);
      setError(err.response?.data?.message || "Failed to load schools");
      setLoading(false);
    });
  }, [fetchSchools]);

  useEffect(() => {
    if (!selectedSchoolId) return;
    fetchCalendar(selectedSchoolId);
  }, [fetchCalendar, selectedSchoolId]);

  const selectedSchool = useMemo(
    () => schools.find((school) => String(school.id) === selectedSchoolId) || null,
    [schools, selectedSchoolId]
  );

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return data.events.filter((event) => new Date(event.date) >= now).slice(0, 5);
  }, [data.events]);

  const groupedEvents = useMemo(() => {
    return data.events.reduce<Record<string, CalendarEvent[]>>((acc, event) => {
      const monthKey = new Date(event.date).toLocaleDateString([], { month: "long", year: "numeric" });
      if (!acc[monthKey]) acc[monthKey] = [];
      acc[monthKey].push(event);
      return acc;
    }, {});
  }, [data.events]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedSchoolId) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await axios.post(
        getApiUrl("school/calendar/events"),
        {
          tenantId: selectedSchoolId,
          name: form.name.trim(),
          date: form.date,
          endDate: form.endDate || null,
          location: form.location.trim(),
          category: form.category,
          responsibleTeacher: form.responsibleTeacher.trim(),
          description: form.description.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Calendar event saved successfully.");
      setForm({
        name: "",
        date: "",
        endDate: "",
        location: "",
        category: "general",
        responsibleTeacher: "",
        description: ""
      });
      await fetchCalendar(selectedSchoolId);
    } catch (err: any) {
      console.error("Error saving superadmin calendar event:", err);
      setError(err.response?.data?.message || "Failed to save calendar event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!selectedSchoolId) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await axios.delete(getApiUrl(`school/calendar/events/${eventId}`), {
        headers: { Authorization: `Bearer ${token}` },
        params: { tenantId: selectedSchoolId }
      });
      setSuccess("Calendar event deleted successfully.");
      await fetchCalendar(selectedSchoolId);
    } catch (err: any) {
      console.error("Error deleting superadmin calendar event:", err);
      setError(err.response?.data?.message || "Failed to delete calendar event");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !selectedSchoolId) {
    return (
      <div className="school-calendar-page">
        <div className="school-calendar-empty">Loading calendar workspace…</div>
      </div>
    );
  }

  return (
    <div className="school-calendar-page superadmin-calendar-page">
      <div className="school-calendar-hero">
        <div>
          <p className="school-calendar-eyebrow">Superadmin Calendar</p>
          <h1>School Calendar Control Centre</h1>
          <p>Review calendar timelines across schools, switch context quickly, and manage published events without leaving the superadmin dashboard.</p>
        </div>
        <div className="school-calendar-hero-stats">
          <div>
            <span>Schools</span>
            <strong>{schools.length}</strong>
          </div>
          <div>
            <span>Selected school</span>
            <strong>{selectedSchool ? selectedSchool.name : "None"}</strong>
          </div>
        </div>
      </div>

      {error ? <div className="school-calendar-feedback error">{error}</div> : null}
      {success ? <div className="school-calendar-feedback success">{success}</div> : null}

      <section className="school-calendar-card superadmin-calendar-selector-card">
        <div className="school-calendar-card-header">
          <div>
            <p className="school-calendar-eyebrow">School Context</p>
            <h2>Choose School</h2>
          </div>
          <button type="button" className="school-calendar-primary-btn school-calendar-secondary-btn" onClick={() => selectedSchoolId && fetchCalendar(selectedSchoolId)} disabled={loading}>
            <RefreshCw size={16} />
            <span>{loading ? "Refreshing…" : "Refresh"}</span>
          </button>
        </div>
        <div className="superadmin-calendar-selector">
          <label>
            School
            <select value={selectedSchoolId} onChange={(event) => setSelectedSchoolId(event.target.value)}>
              {schools.length === 0 ? <option value="">No schools found</option> : null}
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name} {school.subdomain ? `(${school.subdomain})` : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="superadmin-calendar-school-meta">
            <span><Building2 size={15} /> {selectedSchool?.status || "Unknown status"}</span>
            <span><CalendarDays size={15} /> {data.events.length} events</span>
          </div>
        </div>
      </section>

      <div className="school-calendar-grid">
        <section className="school-calendar-card">
          <div className="school-calendar-card-header">
            <div>
              <p className="school-calendar-eyebrow">Highlights</p>
              <h2>{data.schoolName} Upcoming Events</h2>
            </div>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="school-calendar-empty">
              <CalendarDays size={26} />
              <p>No upcoming events have been published for this school yet.</p>
            </div>
          ) : (
            <div className="school-calendar-event-list">
              {upcomingEvents.map((eventItem) => (
                <article key={eventItem.id} className="school-calendar-event">
                  <div className="school-calendar-event-date">
                    <strong>{new Date(eventItem.date).toLocaleDateString([], { day: "2-digit" })}</strong>
                    <span>{new Date(eventItem.date).toLocaleDateString([], { month: "short" })}</span>
                  </div>
                  <div className="school-calendar-event-copy">
                    <h3>{eventItem.name}</h3>
                    <p>{eventItem.description || "No event notes provided."}</p>
                    <div className="school-calendar-event-meta">
                      <span><Clock3 size={14} /> {formatDate(eventItem.date)}</span>
                      {eventItem.location ? <span><MapPin size={14} /> {eventItem.location}</span> : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="school-calendar-card">
          <div className="school-calendar-card-header">
            <div>
              <p className="school-calendar-eyebrow">Academic Calendar</p>
              <h2>Term Dates</h2>
            </div>
          </div>
          {data.terms.length === 0 ? (
            <div className="school-calendar-empty">
              <CalendarDays size={26} />
              <p>No academic terms are configured for this school yet.</p>
            </div>
          ) : (
            <div className="school-calendar-term-list">
              {data.terms.map((term) => (
                <div key={term.id} className="school-calendar-term">
                  <strong>{term.name || `Term ${term.term || ""}`.trim()}</strong>
                  <span>{term.year || ""}</span>
                  <p>{formatDate(term.startDate)} to {formatDate(term.endDate)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="school-calendar-card">
        <div className="school-calendar-card-header">
          <div>
            <p className="school-calendar-eyebrow">Manage Selected School</p>
            <h2>Add Event</h2>
          </div>
        </div>

        <form className="school-calendar-form" onSubmit={handleSubmit}>
          <input name="name" value={form.name} onChange={handleChange} placeholder="Event title" required />
          <input name="date" type="date" value={form.date} onChange={handleChange} required />
          <input name="endDate" type="date" value={form.endDate} onChange={handleChange} />
          <input name="location" value={form.location} onChange={handleChange} placeholder="Location" />
          <select name="category" value={form.category} onChange={handleChange}>
            <option value="general">General</option>
            <option value="academic">Academic</option>
            <option value="exam">Exam</option>
            <option value="sports">Sports</option>
          </select>
          <input name="responsibleTeacher" value={form.responsibleTeacher} onChange={handleChange} placeholder="Responsible staff member" />
          <textarea name="description" value={form.description} onChange={handleChange} placeholder="Event notes" rows={4} />
          <button type="submit" className="school-calendar-primary-btn" disabled={saving || !selectedSchoolId}>
            <Plus size={16} />
            <span>{saving ? "Saving…" : "Save Event"}</span>
          </button>
        </form>
      </section>

      <section className="school-calendar-card">
        <div className="school-calendar-card-header">
          <div>
            <p className="school-calendar-eyebrow">All Events</p>
            <h2>{data.schoolName} Timeline</h2>
          </div>
        </div>

        {data.events.length === 0 ? (
          <div className="school-calendar-empty">
            <CalendarDays size={26} />
            <p>No calendar events have been added for this school yet.</p>
          </div>
        ) : (
          <div className="school-calendar-month-groups">
            {Object.entries(groupedEvents).map(([month, events]) => (
              <div key={month} className="school-calendar-month-group">
                <h3>{month}</h3>
                <div className="school-calendar-month-events">
                  {events.map((eventItem) => (
                    <article key={eventItem.id} className="school-calendar-month-event">
                      <div>
                        <strong>{eventItem.name}</strong>
                        <p>{formatDate(eventItem.date)}</p>
                        {eventItem.description ? <span>{eventItem.description}</span> : null}
                      </div>
                      <div className="school-calendar-month-actions">
                        {eventItem.location ? <small>{eventItem.location}</small> : null}
                        <button type="button" onClick={() => handleDelete(eventItem.id)} disabled={saving} aria-label={`Delete ${eventItem.name}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SuperadminCalendar;
